import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles, XCircle } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [eventId, setEventId] = useState(null);
  const [tier, setTier] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setEventId(data.event_id);
        setTier(data.tier);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["expired","failed"].includes(data.payment_status)) { setStatus(data.payment_status); return; }
      } catch (e) {
        if (attempts > 20) { setStatus("failed"); return; }
      }
      if (attempts > 30) { setStatus("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="grain relative min-h-screen">
      <Header />
      <main className="pt-32 pb-24 max-w-2xl mx-auto px-6 text-center">
        {status === "polling" && (
          <>
            <div className="mx-auto h-20 w-20 rounded-full bg-[#FA003F]/10 grid place-items-center mb-6">
              <Loader2 className="h-10 w-10 text-[#FA003F] animate-spin" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Confirming your payment…</h1>
            <p className="text-gray-600 mt-4 text-lg">Hang tight — this usually takes just a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto h-20 w-20 rounded-full bg-emerald-100 grid place-items-center mb-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-bold tracking-widest uppercase text-[#FA003F] mb-4">
              <Sparkles className="h-3.5 w-3.5" /> Boost activated
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Your event is now sponsored 🎉</h1>
            <p className="text-gray-600 mt-4 text-lg">
              Your listing has been pinned to the hero and marked as sponsored. Locals will see it first.
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              {eventId && (
                <Button onClick={() => nav(`/event/${eventId}`)} className="rounded-full h-12 px-6 bg-[#FA003F] hover:bg-[#C40031]">
                  View my event
                </Button>
              )}
              <Button variant="outline" onClick={() => nav("/")} className="rounded-full h-12 px-6">Back to home</Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <div className="mx-auto h-20 w-20 rounded-full bg-red-100 grid place-items-center mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">We couldn't confirm the payment.</h1>
            <p className="text-gray-600 mt-4 text-lg">
              If you were charged, ping us and we'll sort it out. Otherwise, try again.
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/"><Button className="rounded-full h-12 px-6 bg-[#1A1A1A] hover:bg-[#FA003F]">Back to home</Button></Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
